import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccountService, Combination, Price, PriceConditions, Product } from 'src/api';
import { SeatUtilsService } from './seat-utils.service';
import {
  DEUBNER_CONTENT_FEATURE,
  DEUBNER_TAX_CONTENT_FEATURE,
  isAddonForFeature,
  isCreditsAddonProduct,
} from '../utils/seat-product.util';
import { ZARD_ICONS, ZardIcon } from '../components/ui-components/icon/icons';
import {ceilAndFormat} from '../utils/ui-utils/number';
import { buildIntegrationAddOnsByRid, findIntegrationAddOnForConnectorRid } from '../utils/integration-addon.util';
import { applicableCombination } from '../utils/combination.util';

interface PriceAndProduct {
  price: Price;
  product: Product;
}

export interface SeatPriceOption {
  value: string | undefined;
  /** All price ids of the seat product, so legacy prices still resolve to this option. */
  priceIds?: string[];
  icon?: ZardIcon;
  name: string;
  /** Formatted for display; `creditsValue` is the number it came from, kept for ordering. */
  credits?: string;
  creditsValue: number;
  price?: string;
}

/**
 * Service for managing seat and add-on options.
 * Provides centralized logic for loading and accessing seat types, add-ons, and related data.
 */
@Injectable({
  providedIn: 'root'
})
export class SeatOptionsService {
  private accountService = inject(AccountService);
  private seatUtils = inject(SeatUtilsService);

  // Icon mapping for account types
  private readonly accountTypeIcons: { productName: string; icon: ZardIcon }[] = [
    { productName: "Standard", icon: ZARD_ICONS["layers-2"] },
    { productName: "Komplex", icon: ZARD_ICONS.boxes },
    { productName: "Premium", icon: ZARD_ICONS.crown },
  ];

  // Cached data
  readonly seatPriceOptions = signal<SeatPriceOption[]>([]);
  readonly availableAddOns = signal<Product[]>([]);
  readonly integrationAddOnsByRid = signal<Map<string, Product>>(new Map());
  readonly deubnerAddon = signal<Product | null>(null);
  readonly deubnerTaxAddon = signal<Product | null>(null);
  readonly creditsAddon = signal<Product | null>(null);
  readonly combinations = signal<Combination[]>([]);
  readonly isLoaded = signal(false);

  /**
   * Loads seat options and add-ons from the API.
   * Caches the results for subsequent calls.
   * @returns Promise that resolves when data is loaded
   */
  async loadSeatOptions(): Promise<void> {
    if (this.isLoaded() && this.seatPriceOptions().some((option) => !!option.value)) {
      return;
    }

    try {
      const priceConditions = await firstValueFrom(
        this.accountService.getPriceConditions()
      );

      this.applyPriceConditions(priceConditions);
      this.isLoaded.set(true);
    } catch (error) {
      console.error('Error loading seat options:', error);
      throw error;
    }
  }

  private applyPriceConditions(priceConditions: PriceConditions): void {
    // Seat dropdown options use priceConditions below; sync SeatUtilsService for helpers elsewhere.
    this.seatUtils.setPriceConditions(priceConditions);

    const findPriceAndProduct = (priceId: string | undefined | null): PriceAndProduct | null => {
      if (!priceId) {
        return null;
      }
      const price = priceConditions.prices.find((p) => p.id === priceId);
      if (!price) {
        return null;
      }
      const product = priceConditions.products.find((p) => p.price_ids?.includes(price.id));
      if (!product) {
        return null;
      }
      return { price, product };
    };

    const seatOptions = priceConditions.products
      .filter((product) => product.metadata?.['type'] === 'seat')
      .map((seat) => {
        // The seat is offered at the interval the firm is billed on, not at the product default:
        // Stripe rejects a subscription whose items disagree on the interval.
        const defaultPriceProduct = findPriceAndProduct(this.seatUtils.bookingPriceId(seat));
        const productName = defaultPriceProduct?.product?.name.split('-')[0]?.trim() ?? '';
        const productIcon = this.accountTypeIcons.find((a) => a.productName === productName)?.icon;
        // Resolved from the price, which may state its own allowance and override its product's.
        const creditsValue = this.seatUtils.creditsForPrice(defaultPriceProduct?.price.id);

        return {
          value: defaultPriceProduct?.price.id,
          priceIds: seat.price_ids ?? [],
          icon: productIcon,
          name: productName,
          credits: ceilAndFormat(creditsValue),
          creditsValue,
          price: this.seatUtils.formatPrice(defaultPriceProduct?.price.unit_amount ?? 0),
        };
      })
      .filter((option) => !!option.value)
      .sort((a, b) => a.creditsValue - b.creditsValue);

    const addons = priceConditions.products.filter((product) => product.metadata?.['type'] === 'add_on');

    const deubner = addons.find((addon) => this.isDeubnerAddon(addon)) ?? null;
    const deubnerTax = addons.find((addon) => this.isDeubnerTaxAddon(addon)) ?? null;
    const credits = addons.find((addon) => this.isCreditsAddon(addon)) ?? null;

    this.seatPriceOptions.set(seatOptions);
    this.availableAddOns.set(addons);
    this.integrationAddOnsByRid.set(buildIntegrationAddOnsByRid(addons));
    this.deubnerAddon.set(deubner);
    this.deubnerTaxAddon.set(deubnerTax);
    this.creditsAddon.set(credits);
    this.combinations.set(priceConditions.combinations ?? []);
  }

  /** "Deubner + Deubner Steuern": the add-on names a combination is made of. */
  combinationProductNames(combination: Combination): string {
    const names = new Map(this.availableAddOns().map((addon) => [addon.id, addon.name]));
    return combination.product_ids.map((id) => names.get(id) ?? id).join(' + ');
  }

  /** The combination discount `product` gets when booked with the `selected` products. */
  combinationFor(product: Product | null, selected: readonly (Product | null)[]): Combination | undefined {
    const selectedIds = new Set(selected.filter((p): p is Product => !!p).map((p) => p.id));
    return applicableCombination(product?.id, selectedIds, this.combinations());
  }

  integrationAddOnForRid(rid: string | undefined | null): Product | null {
    return findIntegrationAddOnForConnectorRid(this.integrationAddOnsByRid(), rid);
  }

  integrationPriceIdForRid(rid: string | undefined | null): string | undefined {
    // Interval-aware: a monthly price on a yearly subscription is rejected by Stripe.
    return this.seatUtils.bookingPriceId(this.integrationAddOnForRid(rid));
  }

  /** Every price the integration was sold at, archived ones included; for ownership checks. */
  integrationPriceIdsForRid(rid: string | undefined | null): string[] {
    return this.integrationAddOnForRid(rid)?.price_ids ?? [];
  }

  /**
   * Gets the icon for a specific account type by name.
   * @param productName - The name of the product (e.g., "Standard", "Premium")
   * @returns The icon or undefined if not found
   */
  getIconForAccountType(productName: string): ZardIcon | undefined {
    return this.accountTypeIcons.find((a) => a.productName === productName)?.icon;
  }

  /** The Deubner law content add-on. Matched by feature: both Deubner packages carry the name. */
  isDeubnerAddon(product: Product): boolean {
    return isAddonForFeature(product, DEUBNER_CONTENT_FEATURE);
  }

  /** The Deubner tax content add-on, booked independently of the law content. */
  isDeubnerTaxAddon(product: Product): boolean {
    return isAddonForFeature(product, DEUBNER_TAX_CONTENT_FEATURE);
  }

  /** @see isCreditsAddonProduct */
  isCreditsAddon(product: Product): boolean {
    return isCreditsAddonProduct(product);
  }

  /** Returns the addon when it can be booked for the given seat, otherwise null. */
  getAddonForSeat(addon: Product | null, seatPriceId: string | null | undefined): Product | null {
    if (!addon || !seatPriceId) {
      return null;
    }
    return this.seatUtils.isAddonCompatibleWithSeat(addon, seatPriceId) ? addon : null;
  }

  getDeubnerAddonForSeat(seatPriceId: string | null | undefined): Product | null {
    return this.getAddonForSeat(this.deubnerAddon(), seatPriceId);
  }

  getDeubnerTaxAddonForSeat(seatPriceId: string | null | undefined): Product | null {
    return this.getAddonForSeat(this.deubnerTaxAddon(), seatPriceId);
  }

  getCreditsAddonForSeat(seatPriceId: string | null | undefined): Product | null {
    return this.getAddonForSeat(this.creditsAddon(), seatPriceId);
  }

  /**
   * Resets the cached data, forcing a reload on next access.
   * Useful when data needs to be refreshed (e.g., after subscription changes).
   */
  reset(): void {
    this.isLoaded.set(false);
    this.seatPriceOptions.set([]);
    this.availableAddOns.set([]);
    this.integrationAddOnsByRid.set(new Map());
    this.deubnerAddon.set(null);
    this.deubnerTaxAddon.set(null);
    this.creditsAddon.set(null);
    this.combinations.set([]);
  }
}
